import { useNavigate, Link } from "react-router-dom";
import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getUserWallet, getUserTransactions } from "../services/walletServiceEnhanced";
import DepositModal from "../components/DepositModal";
import BuyModal from "../components/BuyModal";
import { getReferralCode, getReferralStats } from "../services/referralService";
import { txLabel, isCredit, isReversed, timeAgo, formatUsd, txRowAmountColor, txAmountPrefix } from "../utils/transactionDisplay";

function getGreeting() {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    if (h < 21) return 'Good evening';
    return 'Good night';
}

function Welcome() {
    const { user, profileData } = useAuth();
    const navigate = useNavigate();

    const [wallet, setWallet] = useState({ usd: 0, address: '', provisionedAt: null, loading: true, error: null });
    const [referral, setReferral] = useState({ code: '', earned: 0, count: 0, commission: 10, loading: true });
    const [recentTx, setRecentTx] = useState({ data: [], loading: true });
    const [copied, setCopied] = useState(false);
    const [copiedRef, setCopiedRef] = useState(false);
    const [showDepositModal, setShowDepositModal] = useState(false);
    const [showBuyModal, setShowBuyModal] = useState(false);

    const loadData = useCallback(async () => {
        if (!user) return;

        const [walletResult, codeResult, statsResult, txResult] = await Promise.all([
            getUserWallet(user.id),
            getReferralCode(user.id),
            getReferralStats(user.id),
            getUserTransactions(user.id, 3),
        ]);

        if (!walletResult.error && walletResult.data) {
            setWallet({
                usd: walletResult.data.balance_usd || 0,
                address: walletResult.data.wallet_address || '',
                provisionedAt: walletResult.data.provisioned_at || null,
                loading: false,
                error: null,
            });
        } else {
            // Don't render a failed read as a genuine $0.00 — that is indistinguishable
            // from an empty wallet and would hide a missing referral bonus.
            setWallet(prev => ({
                ...prev,
                loading: false,
                error: walletResult.error || 'Unable to load balance',
            }));
        }

        setReferral({
            code: codeResult.referralCode || '',
            earned: Number(statsResult.stats?.totalEarned) || 0,
            count: statsResult.stats?.totalReferrals || 0,
            commission: statsResult.stats?.currentCommission ?? 10,
            loading: false,
        });

        setRecentTx({
            data: txResult.data || [],
            loading: false,
        });
    }, [user]);

    useEffect(() => {
        if (!user) { navigate('/sign-in'); return; }
        const role = String(user?.user_metadata?.role || localStorage.getItem('role') || '');
        if (role === '4') { navigate('/admin'); return; }
        loadData();
    }, [user, navigate, loadData]);

    const copyAddress = () => {
        if (!wallet.address) return;
        navigator.clipboard.writeText(wallet.address);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const copyReferralLink = () => {
        if (!referral.code) return;
        navigator.clipboard.writeText(`${window.location.origin}/sign-up?ref=${referral.code}`);
        setCopiedRef(true);
        setTimeout(() => setCopiedRef(false), 2000);
    };

    const firstName = (user?.user_metadata?.full_name || profileData?.fullName || 'there').split(' ')[0];
    const avatarUrl = profileData?.profileImage || null;
    const initials = firstName.charAt(0).toUpperCase();

    // Only a server-provisioned wallet has a real, controllable address.
    const isProvisioned = Boolean(wallet.provisionedAt && wallet.address);

    const formatAddress = (addr) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '—';

    return (
        <>
            <main className="main-sectionWrapper">
                <div id="main-section" className="primary-scroll">

                    {/* ── Header ───────────────────────────────────────── */}
                    <div style={s.header}>
                        <div style={s.headerContent}>
                            <span style={s.logoText}>Sigma Bank</span>
                            <div style={s.headerRight}>
                                {avatarUrl ? (
                                    <img src={avatarUrl} alt={firstName} style={s.headerAvatar} />
                                ) : (
                                    <div style={s.headerAvatarPlaceholder}>{initials}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={s.container}>

                        {/* ── Greeting ─────────────────────────────────── */}
                        <div style={s.greeting}>
                            <span style={s.greetingTime}>{getGreeting()},</span>
                            <span style={s.greetingName}>{firstName}</span>
                        </div>

                        {/* ── Balance Hero Card ────────────────────────── */}
                        <div style={s.balanceCard}>
                            <div style={s.balanceCardGlow} aria-hidden="true" />
                            <div style={s.balanceTopRow}>
                                {/* Off-chain USD only — on-chain value lives on /wallet as
                                    "Crypto Deposits", so this is not a combined total. */}
                                <span style={s.balanceLabel}>Spendable Balance</span>
                            </div>
                            {wallet.error && !wallet.loading ? (
                                <div style={s.balanceErrorBox}>
                                    <span style={s.balanceErrorText}>
                                        <i className="bx bx-error-circle" aria-hidden="true" /> Couldn't load your balance
                                    </span>
                                    <button onClick={loadData} style={s.balanceRetryBtn}>Retry</button>
                                </div>
                            ) : (
                                <>
                                    <div style={s.balanceUsd}>
                                        {wallet.loading
                                            ? <div style={s.balanceSkeleton} aria-label="Loading balance" />
                                            : `$${formatUsd(wallet.usd)}`
                                        }
                                    </div>
                                    {isProvisioned ? (
                                        <div style={s.addressRow}>
                                            <i className="bx bx-chip" style={s.addressIcon} aria-hidden="true" />
                                            <span style={s.addressText}>{formatAddress(wallet.address)}</span>
                                            <button onClick={copyAddress} style={s.copyBtn} aria-label="Copy wallet address">
                                                <i className={`bx ${copied ? 'bx-check' : 'bx-copy'}`} />
                                                {copied ? ' Copied' : ' Copy'}
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={s.addressRow}>
                                            <i className="bx bx-loader-alt bx-spin" style={s.addressIcon} aria-hidden="true" />
                                            <span style={s.addressText}>Deposit address being prepared…</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* ── Quick Action Buttons ─────────────────────── */}
                        <div style={s.quickActions}>
                            <Link to="/wallet?action=send" style={s.qaBtn}>
                                <div style={{ ...s.qaIcon, background: 'rgba(59,130,246,0.15)' }}>
                                    <i className="bx bx-send" style={{ color: '#3b82f6', fontSize: 22 }} />
                                </div>
                                <span style={s.qaLabel}>Send</span>
                            </Link>
                            <button
                                onClick={() => setShowDepositModal(true)}
                                disabled={!isProvisioned}
                                title={isProvisioned ? undefined : 'Your deposit address is still being prepared'}
                                style={{ ...s.qaBtn, ...(isProvisioned ? {} : s.qaBtnDisabled) }}
                            >
                                <div style={{ ...s.qaIcon, background: 'rgba(25,191,177,0.15)' }}>
                                    <i className="bx bx-download" style={{ color: '#19bfb1', fontSize: 22 }} />
                                </div>
                                <span style={s.qaLabel}>Deposit</span>
                            </button>
                            <button
                                onClick={() => setShowBuyModal(true)}
                                disabled={!isProvisioned}
                                title={isProvisioned ? undefined : 'Your deposit address is still being prepared'}
                                style={{ ...s.qaBtn, ...(isProvisioned ? {} : s.qaBtnDisabled) }}
                            >
                                <div style={{ ...s.qaIcon, background: 'rgba(167,139,250,0.15)' }}>
                                    <i className="bx bx-credit-card" style={{ color: '#a78bfa', fontSize: 22 }} />
                                </div>
                                <span style={s.qaLabel}>Buy</span>
                            </button>
                        </div>

                        {/* ── Referral Banner ──────────────────────────── */}
                        <div style={s.referralCard}>
                            <div style={s.referralCardGlow} aria-hidden="true" />
                            <div style={s.referralTop}>
                                <div style={s.referralIconWrap}>
                                    <i className="bx bx-gift" style={{ color: '#a78bfa', fontSize: 22 }} />
                                </div>
                                <div style={s.referralTextBlock}>
                                    <div style={s.referralTitle}>Invite &amp; Earn</div>
                                    <div style={s.referralSubtitle}>${referral.commission.toFixed(2)} per successful referral</div>
                                </div>
                                <div style={s.referralEarned}>
                                    <div style={s.referralEarnedAmount}>${formatUsd(referral.earned)}</div>
                                    <div style={s.referralEarnedLabel}>Earned</div>
                                </div>
                            </div>
                            <div style={s.referralBottom}>
                                <div style={s.referralStats}>
                                    <div style={s.refStat}>
                                        <span style={s.refStatVal}>{referral.count}</span>
                                        <span style={s.refStatLbl}>Referrals</span>
                                    </div>
                                    <div style={s.refDivider} />
                                    <div style={s.refStat}>
                                        <span style={s.refStatVal}>{referral.code || '...'}</span>
                                        <span style={s.refStatLbl}>Your Code</span>
                                    </div>
                                </div>
                                <button onClick={copyReferralLink} style={s.referralShareBtn}>
                                    <i className={`bx ${copiedRef ? 'bx-check' : 'bx-share-alt'}`} />
                                    {copiedRef ? 'Copied!' : 'Share'}
                                </button>
                            </div>
                        </div>

                        {/* ── Recent Activity ───────────────────────────── */}
                        <div style={s.sectionHeader}>
                            <span style={s.sectionTitle}>Recent Activity</span>
                            <Link to="/account-statement" style={s.viewAll}>View All</Link>
                        </div>

                        <div style={s.activityCard}>
                            {recentTx.loading ? (
                                [1, 2, 3].map(i => (
                                    <div key={i} style={s.txSkeletonRow}>
                                        <div style={s.txSkeletonIcon} />
                                        <div style={{ flex: 1 }}>
                                            <div style={s.txSkeletonLine} />
                                            <div style={{ ...s.txSkeletonLine, width: '60%', marginTop: 6 }} />
                                        </div>
                                    </div>
                                ))
                            ) : recentTx.data.length === 0 ? (
                                <div style={s.emptyState}>
                                    <div style={s.emptyIcon}>
                                        <i className="bx bx-transfer" style={{ fontSize: 28, color: 'rgba(255,255,255,0.3)' }} />
                                    </div>
                                    <p style={s.emptyText}>No transactions yet</p>
                                    <p style={s.emptySubtext}>Send or receive funds to get started</p>
                                </div>
                            ) : (
                                recentTx.data.map((tx, idx) => {
                                    const incoming = isCredit(tx.transaction_type);
                                    return (
                                        <div key={tx.id || idx} style={{ ...s.txRow, ...(idx < recentTx.data.length - 1 ? s.txRowDivider : {}) }}>
                                            <div style={{ ...s.txIconWrap, background: incoming ? 'rgba(5,150,105,0.15)' : 'rgba(239,68,68,0.15)' }}>
                                                <i
                                                    className={`bx ${incoming ? 'bx-arrow-to-bottom' : 'bx-arrow-from-bottom'}`}
                                                    style={{ color: txRowAmountColor(tx, incoming), fontSize: 18 }}
                                                />
                                            </div>
                                            <div style={s.txInfo}>
                                                <div style={s.txLabel}>{txLabel(tx)}</div>
                                                <div style={s.txTime}>{timeAgo(tx.created_at)}</div>
                                            </div>
                                            <div style={{
                                                ...s.txAmount,
                                                color: txRowAmountColor(tx, incoming),
                                                textDecoration: isReversed(tx) ? 'line-through' : 'none',
                                            }}>
                                                {txAmountPrefix(tx, incoming)}${formatUsd(tx.amount_usd || 0)}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                    </div>
                </div>
            </main>

            {/* ── Deposit Modal ─────────────────────────────────────────── */}
            {showDepositModal && isProvisioned && (
                <DepositModal
                    walletAddress={wallet.address}
                    onClose={() => setShowDepositModal(false)}
                />
            )}

            {/* ── Buy Modal (MoonPay) ───────────────────────────────────── */}
            {showBuyModal && isProvisioned && (
                <BuyModal
                    walletAddress={wallet.address}
                    userId={user?.id}
                    onClose={() => setShowBuyModal(false)}
                    onCompleted={loadData}
                />
            )}

            <style>{`
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(16px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 0.35; }
                    50%       { opacity: 0.7; }
                }
                @keyframes livePulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%       { opacity: 0.5; transform: scale(0.8); }
                }
                @keyframes sheetUp {
                    from { transform: translateY(100%); }
                    to   { transform: translateY(0); }
                }
            `}</style>
        </>
    );
}

const s = {
    /* ── Header ─────────────────────────────────────────────────────── */
    header: {
        background: 'rgba(14,7,88,0.85)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        padding: '14px 20px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    headerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    logoText: {
        color: '#fff',
        fontSize: 18,
        fontWeight: 700,
        letterSpacing: '-0.3px',
    },
    headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
    headerAvatar: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        objectFit: 'cover',
        border: '2px solid rgba(25,191,177,0.4)',
    },
    headerAvatarPlaceholder: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'linear-gradient(135deg,#19bfb1 0%,#0e9688 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: 15,
        fontWeight: 700,
        border: '2px solid rgba(25,191,177,0.4)',
    },

    /* ── Container ──────────────────────────────────────────────────── */
    container: {
        padding: '20px 16px 96px',
        animation: 'fadeInUp 0.4s ease-out',
    },

    /* ── Greeting ───────────────────────────────────────────────────── */
    greeting: {
        marginBottom: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    },
    greetingTime: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 14,
    },
    greetingName: {
        color: '#fff',
        fontSize: 24,
        fontWeight: 700,
        letterSpacing: '-0.3px',
    },

    /* ── Balance card ───────────────────────────────────────────────── */
    balanceCard: {
        position: 'relative',
        background: 'linear-gradient(145deg, rgba(25,191,177,0.18) 0%, rgba(14,7,88,0.7) 60%)',
        border: '1px solid rgba(25,191,177,0.22)',
        borderRadius: 22,
        padding: '22px 20px 18px',
        marginBottom: 16,
        overflow: 'hidden',
    },
    balanceCardGlow: {
        position: 'absolute',
        top: -40,
        right: -40,
        width: 140,
        height: 140,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(25,191,177,0.18) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    balanceTopRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    balanceLabel: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 1.2,
        fontWeight: 600,
    },
    ethRateBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: 'rgba(25,191,177,0.12)',
        border: '1px solid rgba(25,191,177,0.25)',
        color: '#19bfb1',
        fontSize: 11,
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: 20,
    },
    liveDot: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: '#19bfb1',
        display: 'inline-block',
        animation: 'livePulse 2s ease infinite',
    },
    balanceUsd: {
        color: '#fff',
        fontSize: 38,
        fontWeight: 800,
        letterSpacing: '-1px',
        marginBottom: 4,
        minHeight: 46,
    },
    balanceSkeleton: {
        width: 180,
        height: 42,
        background: 'rgba(255,255,255,0.08)',
        borderRadius: 10,
        animation: 'pulse 1.5s ease infinite',
    },
    balanceEth: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 13,
        marginBottom: 18,
    },
    balanceErrorBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        minHeight: 46,
        marginBottom: 4,
    },
    balanceErrorText: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        color: '#fca5a5',
        fontSize: 14,
        fontWeight: 600,
    },
    balanceRetryBtn: {
        background: 'rgba(255,255,255,0.12)',
        border: '1px solid rgba(255,255,255,0.2)',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
        padding: '6px 14px',
        borderRadius: 8,
        cursor: 'pointer',
    },
    addressRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'rgba(0,0,0,0.22)',
        borderRadius: 12,
        padding: '10px 14px',
    },
    addressIcon: {
        color: 'rgba(255,255,255,0.35)',
        fontSize: 16,
    },
    addressText: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 14,
        fontFamily: 'monospace',
        flex: 1,
        letterSpacing: 0.5,
    },
    copyBtn: {
        background: 'rgba(25,191,177,0.15)',
        border: '1px solid rgba(25,191,177,0.3)',
        color: '#19bfb1',
        borderRadius: 8,
        padding: '5px 12px',
        fontSize: 12,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
        fontWeight: 600,
    },

    /* ── Quick Actions ──────────────────────────────────────────────── */
    quickActions: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 12,
        marginBottom: 20,
    },
    qaBtn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: '18px 12px',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'background 0.15s',
    },
    qaBtnDisabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
    },
    qaIcon: {
        width: 48,
        height: 48,
        borderRadius: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    qaLabel: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: 0.2,
    },

    /* ── Referral card ──────────────────────────────────────────────── */
    referralCard: {
        position: 'relative',
        background: 'linear-gradient(145deg, rgba(124,58,237,0.2) 0%, rgba(14,7,88,0.6) 100%)',
        border: '1px solid rgba(124,58,237,0.25)',
        borderRadius: 20,
        padding: '18px 16px 14px',
        marginBottom: 24,
        overflow: 'hidden',
    },
    referralCardGlow: {
        position: 'absolute',
        bottom: -50,
        left: -30,
        width: 130,
        height: 130,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124,58,237,0.2) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    referralTop: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 14,
    },
    referralIconWrap: {
        width: 44,
        height: 44,
        borderRadius: 12,
        background: 'rgba(124,58,237,0.2)',
        border: '1px solid rgba(124,58,237,0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    referralTextBlock: {
        flex: 1,
    },
    referralTitle: {
        color: '#fff',
        fontSize: 15,
        fontWeight: 700,
        marginBottom: 1,
    },
    referralSubtitle: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 12,
    },
    referralEarned: {
        textAlign: 'right',
    },
    referralEarnedAmount: {
        color: '#a78bfa',
        fontSize: 18,
        fontWeight: 800,
    },
    referralEarnedLabel: {
        color: 'rgba(255,255,255,0.45)',
        fontSize: 11,
    },
    referralBottom: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: 'rgba(0,0,0,0.2)',
        borderRadius: 12,
        padding: '10px 12px',
    },
    referralStats: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    refStat: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    refStatVal: {
        color: '#fff',
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: 0.5,
    },
    refStatLbl: {
        color: 'rgba(255,255,255,0.45)',
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    refDivider: {
        width: 1,
        height: 28,
        background: 'rgba(255,255,255,0.1)',
    },
    referralShareBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: 'rgba(124,58,237,0.25)',
        border: '1px solid rgba(124,58,237,0.4)',
        color: '#a78bfa',
        borderRadius: 10,
        padding: '8px 14px',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },

    /* ── Section header ─────────────────────────────────────────────── */
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    sectionTitle: {
        color: '#fff',
        fontSize: 16,
        fontWeight: 700,
    },
    viewAll: {
        color: '#19bfb1',
        fontSize: 13,
        fontWeight: 600,
        textDecoration: 'none',
    },

    /* ── Activity card ──────────────────────────────────────────────── */
    activityCard: {
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 18,
        overflow: 'hidden',
    },
    txRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
    },
    txRowDivider: {
        borderBottom: '1px solid rgba(255,255,255,0.05)',
    },
    txIconWrap: {
        width: 40,
        height: 40,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    txInfo: {
        flex: 1,
        minWidth: 0,
    },
    txLabel: {
        color: '#fff',
        fontSize: 14,
        fontWeight: 500,
        marginBottom: 3,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    txTime: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 12,
    },
    txAmount: {
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
    },

    /* ── Skeletons ──────────────────────────────────────────────────── */
    txSkeletonRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
    },
    txSkeletonIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        background: 'rgba(255,255,255,0.07)',
        animation: 'pulse 1.5s ease infinite',
        flexShrink: 0,
    },
    txSkeletonLine: {
        height: 12,
        borderRadius: 6,
        background: 'rgba(255,255,255,0.07)',
        animation: 'pulse 1.5s ease infinite',
        width: '80%',
    },

    /* ── Empty state ────────────────────────────────────────────────── */
    emptyState: {
        textAlign: 'center',
        padding: '32px 20px',
    },
    emptyIcon: {
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 12px',
    },
    emptyText: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 14,
        fontWeight: 500,
        margin: '0 0 4px',
    },
    emptySubtext: {
        color: 'rgba(255,255,255,0.35)',
        fontSize: 12,
        margin: 0,
    },

    /* ── Deposit Modal ──────────────────────────────────────────────── */
    modalOverlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
    },
    modalSheet: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 301,
        background: 'linear-gradient(180deg,#1a0e5a 0%,#0e0758 100%)',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        borderTop: '1px solid rgba(255,255,255,0.1)',
        animation: 'sheetUp 0.28s cubic-bezier(0.32,0.72,0,1)',
        paddingBottom: 'calc(24px + env(safe-area-inset-bottom,0px))',
    },
    modalHandle: {
        width: 40,
        height: 4,
        borderRadius: 2,
        background: 'rgba(255,255,255,0.2)',
        margin: '14px auto 0',
    },
    modalHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '20px 20px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
    },
    modalIconWrap: {
        width: 48,
        height: 48,
        borderRadius: 14,
        background: 'rgba(25,191,177,0.15)',
        border: '1px solid rgba(25,191,177,0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    modalTitle: {
        color: '#fff',
        fontSize: 17,
        fontWeight: 700,
        marginBottom: 2,
    },
    modalSubtitle: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 13,
    },
    modalBody: {
        padding: '20px 20px 8px',
    },
    depositLabel: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 10,
    },
    depositAddressFull: {
        background: 'rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 12,
        padding: '14px 16px',
        color: 'rgba(255,255,255,0.85)',
        fontSize: 13,
        fontFamily: 'monospace',
        wordBreak: 'break-all',
        marginBottom: 14,
        lineHeight: 1.6,
    },
    depositCopyBtn: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '14px',
        borderRadius: 14,
        border: 'none',
        background: 'linear-gradient(135deg,#19bfb1 0%,#0e9688 100%)',
        color: '#fff',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
        marginBottom: 14,
    },
    depositNote: {
        color: 'rgba(255,255,255,0.35)',
        fontSize: 12,
        textAlign: 'center',
        lineHeight: 1.5,
        margin: 0,
    },
    modalCloseBtn: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.1)',
        color: 'rgba(255,255,255,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
    },
};

export default Welcome;
